import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, ProcessDataFromClipboardParams } from 'ag-grid-community';
import { ContractDate, MarketViewVolumeModel } from '../../../../../Generated/Mark-It-View-Api';
import { BASE_PATH } from '../../../../../Generated/Mark-It-View-Api/base';

interface UserVolumeRow {
  month: number; // 1-12
  monthName: string;
  volume: number | null;
}

interface UserVolumeModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: (userVolumeId?: number) => void;
  volume?: MarketViewVolumeModel | null;
  defaultMonths?: ContractDate[];
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const seedEmptyRows = (): UserVolumeRow[] =>
  MONTH_NAMES.map((name, idx) => ({ month: idx + 1, monthName: name, volume: null }));

// Accepts either a raw month number ("1"-"12") or a legacy "YYYY-MM" key.
const parseMonthKey = (key: string): number | null => {
  const trimmed = (key ?? '').trim();
  const direct = Number(trimmed);
  if (Number.isInteger(direct) && direct >= 1 && direct <= 12) return direct;
  const match = /^\d{4}-(0[1-9]|1[0-2])$/.exec(trimmed);
  if (match) return Number(match[1]);
  return null;
};

export default function UserVolumeModal(props: UserVolumeModalProps) {
  const { open, onClose, onSaved, volume, defaultMonths } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [name, setName] = useState<string>('');
  const [rows, setRows] = useState<UserVolumeRow[]>(seedEmptyRows);
  const [isSaving, setIsSaving] = useState(false);
  const gridRef = useRef<AgGridReact<UserVolumeRow>>(null);

  useEffect(() => {
    if (!open) return;

    const fresh = seedEmptyRows();
    if (volume) {
      setName(volume.name ?? '');
      Object.entries(volume.volumes ?? {}).forEach(([key, vol]) => {
        const m = parseMonthKey(key);
        if (m != null) fresh[m - 1].volume = vol as number;
      });
    } else {
      setName('');
    }
    setRows(fresh);
  }, [open, volume, defaultMonths]);

  const columns = useMemo<ColDef<UserVolumeRow>[]>(
    () => [
      {
        headerName: 'Month',
        field: 'monthName',
        editable: false,
        width: 140,
        pinned: 'left',
      },
      {
        headerName: 'Volume',
        field: 'volume',
        editable: true,
        flex: 1,
        valueParser: (params) => {
          const v = params.newValue;
          if (v === '' || v == null) return null;
          const cleaned = typeof v === 'string' ? v.replace(/,/g, '').trim() : v;
          const n = Number(cleaned);
          return Number.isFinite(n) ? n : null;
        },
      },
    ],
    [],
  );

  const onGridReady = (e: GridReadyEvent<UserVolumeRow>) => {
    e.api.sizeColumnsToFit();
  };

  // Handle Excel clipboard paste. Trims any rows beyond the 12 month limit.
  const processDataFromClipboard = (params: ProcessDataFromClipboardParams<UserVolumeRow>) => {
    const data = params.data;
    if (!data || data.length === 0) return null;

    // Trim trailing empty row that Excel often appends.
    const cleaned = data.filter(
      (r, idx) => !(idx === data.length - 1 && r.every((c) => c === '' || c == null)),
    );
    if (cleaned.length === 0) return null;

    const focused = params.api.getFocusedCell();
    const startIndex = focused?.rowIndex ?? 0;
    const maxRows = 12 - startIndex;
    return cleaned.slice(0, Math.max(maxRows, 0));
  };

  const handleCellValueChanged = () => {
    const api = gridRef.current?.api;
    if (!api) return;
    const next: UserVolumeRow[] = [];
    api.forEachNode((node) => {
      if (node.data) next.push(node.data);
    });
    setRows(next);
  };

  const handleSave = () => {
    if (!name.trim()) {
      enqueueSnackbar('Name is required', { variant: 'warning' });
      return;
    }

    // Commit any in-flight cell edit before reading values.
    gridRef.current?.api?.stopEditing();

    // Read row data directly from the grid so we capture pasted/edited values
    // even if React state hasn't synced yet.
    const liveRows: UserVolumeRow[] = [];
    const api = gridRef.current?.api;
    if (api) {
      api.forEachNode((node) => {
        if (node.data) liveRows.push(node.data);
      });
    }
    const source = liveRows.length > 0 ? liveRows : rows;

    const volumes: { [key: string]: number } = {};
    for (const row of source) {
      const v = typeof row.volume === 'string' ? Number(row.volume) : row.volume;
      if (v == null || !Number.isFinite(v)) continue;
      volumes[row.month.toString()] = v as number;
    }

    if (Object.keys(volumes).length === 0) {
      enqueueSnackbar('At least one month with a volume is required', { variant: 'warning' });
      return;
    }

    setIsSaving(true);
    // The generated client serializes dictionary query params as "[object Object]",
    // which causes Volumes to arrive empty on the server. Post directly here and
    // expand the dictionary into Volumes[month]=value entries so model binding works.
    const params = new URLSearchParams();
    params.set('UserVolumeId', String(volume?.userVolumeId ?? 0));
    params.set('Name', name.trim());
    Object.entries(volumes).forEach(([month, vol]) => {
      params.append(`Volumes[${month}]`, String(vol));
    });

    axios
      .post(`${BASE_PATH}/market-view/volumes?${params.toString()}`)
      .then((response) => {
        const data = response?.data as { volumeId?: number; userVolumeId?: number } | undefined;
        const savedId = data?.volumeId ?? data?.userVolumeId ?? volume?.userVolumeId;
        enqueueSnackbar('Volume saved', { variant: 'success' });
        onSaved(savedId);
      })
      .catch(() => {
        enqueueSnackbar('Failed to save volume', { variant: 'error' });
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{volume ? 'Edit User Volume' : 'Add User Volume'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
          />

          <Typography variant="subtitle2">Volumes</Typography>
          <Typography variant="caption" color="text.secondary">
            Tip: copy a 12-cell column from Excel and paste into the Volume column.
          </Typography>

          <div className="ag-theme-alpine" style={{ width: '100%' }}>
            <AgGridReact<UserVolumeRow>
              ref={gridRef}
              rowData={rows}
              columnDefs={columns}
              onGridReady={onGridReady}
              onCellValueChanged={handleCellValueChanged}
              singleClickEdit
              stopEditingWhenCellsLoseFocus
              enableRangeSelection
              enableFillHandle
              processDataFromClipboard={processDataFromClipboard}
              domLayout="autoHeight"
              defaultColDef={{ resizable: true, sortable: false, filter: false }}
            />
          </div>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
